import React, { useCallback, useEffect, useState } from 'react'
import { Platform } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import TransparentHeader from '@zet/common/components/header/transparentHeader'
import { PressableWithIntent } from '@zet/common/components/pressableWithIntent'
import { TopBarTabs } from '@zet/common/components/topBarTabs'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import useMyCustomersStore from '@zet/customer/src/store/myCustomers'
import { BottomSheet, colors, colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { Select } from '@zet/design-system/select'
import { B2, SH1 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import { AddCustomerRoundedOutlined } from '@zet/svg'
import {
  BUTTON_TYPE,
  sortingOptionsForNonVerified,
  sortingOptionsForVerified,
} from '@zet/utils/Constants'
import { toCapitalize } from '@zet/utils/stringConversion'
import { LinearGradient } from 'expo-linear-gradient'

import { useCustomerCount } from '../../api/hooks'
import NonVerifiedCustomers from './NonVerifiedCustomers'
import VerifiedCustomers from './VerifiedCustomers'

function MyCustomers() {
  const [isBottomSheetVisible, setIsBottomSheetVisible] =
    useState<boolean>(false)

  const [isSortingBottomSheetVisible, setIsSortingBottomSheetVisible] =
    useState<boolean>(false)
  const navigation = useNavigation()
  const route = useRoute()
  const { setIsBrandsFlow } = useMyCustomersStore((state) => state)
  const searchedText = useMyCustomersStore((state) => state.searchText)
  const setSearchedText = useMyCustomersStore((state) => state.setSearchText)
  const setSubAgentId = useMyCustomersStore((state) => state.setSubAgentId)

  const sortType = useMyCustomersStore((state) => state.sortType)
  const setSortType = useMyCustomersStore((state) => state.setSortType)

  const nonVerifiedSortType = useMyCustomersStore(
    (state) => state.nonVerifiedSortType,
  )
  const setNonVerifiedSortType = useMyCustomersStore(
    (state) => state.setNonVerifiedSortType,
  )

  const sortingOptions = useMyCustomersStore((state) => state.sortingOptions)

  useEffect(() => {
    if (route?.params?.subAgentId) {
      setSubAgentId(route?.params?.subAgentId)
    }
    setSortType('creditReport')

    return () => {
      setSubAgentId('')
      setSearchedText('')
    }
  }, [route?.params?.subAgentId, setSearchedText, setSubAgentId, setSortType])

  const { data } = useCustomerCount(route?.params?.subAgentId || '')
  const handleSearch = (input: string) => {
    setSearchedText(input)
  }

  const handleVerifiedSortingSelection = (selectedOption: string) => {
    if (sortType !== selectedOption) setSortType(selectedOption)
    publishEvent('click_action', {
      item_name: 'my_customers_verified_sorting_selection',
    })
    setIsSortingBottomSheetVisible(false)
  }

  const handleNonVerifiedSortingSelection = (selectedOption: string) => {
    if (nonVerifiedSortType !== selectedOption)
      setNonVerifiedSortType(selectedOption)
    publishEvent('click_action', {
      item_name: 'my_customers_non_verified_sorting_selection',
    })
    setIsSortingBottomSheetVisible(false)
  }

  const NonVerifiedCustomersComponent = useCallback(
    (props) => (
      <NonVerifiedCustomers status={route?.params?.status} {...props} />
    ),
    [route?.params?.status],
  )

  const sortingOptionsList =
    sortingOptions === 'verified'
      ? sortingOptionsForVerified
      : sortingOptionsForNonVerified

  return (
    <>
      <LinearGradient
        colors={[zetColors.tertiary[100], zetColors.tertiary[100]]}
        start={[0, 0]}
        end={[1, 0.0]}
        locations={[0.0, 1.0]}
      >
        <TransparentHeader
          isCloseIcon
          title={
            route?.params?.subAgentName
              ? toCapitalize(`${route?.params?.subAgentName} Customers`)
              : i18n.t('myCustomers.header')
          }
        />
        <XStack className="items-center justify-between">
          <Stack className="ml-3" style={{ flex: 0.8 }}>
            <Input
              iconLeft={
                <Icon
                  style={{
                    marginTop: -12,
                  }}
                  type="SearchOutlined"
                  color={zetColors.neutral['white100']}
                />
              }
              color={zetColors.neutral['white100']}
              selectionColor={'grey'}
              className="mb-3 border-[#f5f7fb7c]  bg-[#1A304D]"
              placeholder={i18n.t('myCustomers.placeholder')}
              frameStyle={{
                backgroundColor: '#1A304D',
                borderColor: '#f5f7fb7c',
              }}
              style={
                Platform.OS === 'web' ? { color: colors.neutral.white100 } : {}
              }
              onChangeText={handleSearch}
              value={searchedText}
            />
          </Stack>
          <Pressable
            onPress={() => {
              setIsSortingBottomSheetVisible(true)
              publishEvent('click_action', {
                item_name: 'my_customers_sorting',
              })
            }}
            style={{
              flex: 0.2,
              alignItems: 'center',
              marginTop: -10,
            }}
          >
            <Icon
              type="MyCustFiltersIcon"
              size={30}
              color={zetColors.grays[40]}
            />
          </Pressable>
        </XStack>
      </LinearGradient>
      <TopBarTabs
        tabs={[
          {
            TabComponent: VerifiedCustomers,
            tabTitle: i18n.t('myCustomers.verified'),
            subTitle: `${data?.data?.verified || 0}`,
            tabName: 'VerifiedCustomers',
            labelLeftComponent: {
              activeComponent: <Icon type="SecureFilled" color="#5E9FFF" />,
              inActiveComponent: <Icon type="SecureOutlined" />,
            },
          },
          {
            TabComponent: NonVerifiedCustomersComponent,
            tabTitle: i18n.t('myCustomers.nonVerified'),
            subTitle: `${data?.data?.notVerified || 0}`,
            tabName: 'NonVerifiedCustomers',
            labelLeftComponent: {
              activeComponent: (
                <Icon type="VerificationPendingFilled" color="#5E9FFF" />
              ),
              inActiveComponent: <Icon type="VerificationPendingOutlined" />,
            },
          },
        ]}
        isTransparent={false}
        initalRouteName={
          route.params?.setRoute === 'NonVerifiedCustomers'
            ? 'NonVerifiedCustomers'
            : 'VerifiedCustomers'
        }
      />

      <Stack className="border-grays-40 bg-neutral-white100 items-center border-t p-3">
        <PressableWithIntent
          type={BUTTON_TYPE.BUTTON}
          name={'AddCustomer'}
          size={'lg'}
          onPress={() => {
            navigation.navigate('KnowYourCustomer', { source: 'myCustomer' })
            setIsBrandsFlow(false)
          }}
          fullWidth
          eventName="click_action"
          eventPayload={{ item_name: 'add_customer_add_customer' }}
        >
          <XStack className="items-center space-x-2">
            <Stack>
              <AddCustomerRoundedOutlined color={'#fff'} />
            </Stack>

            <B2 className="text-white">{i18n.t('myCustomers.addCust')}</B2>
          </XStack>
        </PressableWithIntent>
      </Stack>
      <BottomSheet
        visible={isBottomSheetVisible}
        // snapPoints={['50%']}
        disableBottomSheet={() => setIsBottomSheetVisible(false)}
        onDismiss={() => setIsBottomSheetVisible(false)}
        sheetName="add_customer_share"
      >
        <XStack className=" justify-between px-3">
          <XStack className="items-center">
            <Icon type="AddUserOutlined" />
            <SH1 className="ml-4/2"> {i18n.t('myCustomers.add')} </SH1>
          </XStack>
          <Pressable
            style={{
              width: 40,
              height: 40,
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onPress={() => setIsBottomSheetVisible(false)}
          >
            <Icon size={24} type="Close" />
          </Pressable>
        </XStack>
        <Stack className="mb-4/2 border-grays-30 border-t-[1px]"></Stack>
        <Stack style={{ marginHorizontal: 16 }} className="mb-4/2 items-center">
          {/* <Image
            source={require('../../assets/images/mobileFrame.png')}
            className="h-[140] w-[155] "
          /> */}
          <B2
            style={{ alignSelf: 'flex-start', textAlign: 'auto' }}
            className=" text-grays-80 "
          >
            {i18n.t('myCustomers.getBetter')}
          </B2>
        </Stack>
        <Stack className="mb-4/2 border-grays-30 border-t-[1px]"></Stack>

        <Stack className="px-3">
          <Button
            onPress={() => {
              setIsBottomSheetVisible(false)
              setSelectedCustomer(null)
              navigation.navigate('KnowYourCustomer')
            }}
            size={'lg'}
            fullWidth
            className="mb-4/2"
            eventName="click_action"
            eventPayload={{
              item_name: 'add_customer_add_customer_bottomsheet',
              cta_name: 'Enter details',
            }}
          >
            {i18n.t('myCustomers.enter')}
          </Button>
          <Button
            onPress={() => handleShareLink()}
            size={'lg'}
            outlined={true}
            fullWidth
            className="mb-4/2 justify-center"
            textStyle="text-primary-100 ml-0"
            iconLeft={<Icon type="Share" color="#2075F0" />}
            eventName="click_action"
            eventPayload={{
              item_name: 'add_customer_add_customer_bottomsheet',
              cta_name: 'Share Link',
            }}
          >
            {i18n.t('myCustomers.share')}
          </Button>
        </Stack>
      </BottomSheet>

      <BottomSheet
        visible={isSortingBottomSheetVisible}
        disableBottomSheet={() => setIsSortingBottomSheetVisible(false)}
        onDismiss={() => setIsSortingBottomSheetVisible(false)}
        sheetName="my_customers_sorting"
      >
        <XStack className=" justify-between px-3">
          <XStack className="items-center">
            <Icon type="MyCustFiltersIcon" color={zetColors.grays[90]} />
            <SH1 className="ml-4/2 text-base text-[#000000]">
              {i18n.t('myCustomers.sortCustomers')}
            </SH1>
          </XStack>
          <Pressable
            style={{
              width: 40,
              height: 40,
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onPress={() => setIsSortingBottomSheetVisible(false)}
          >
            <Icon size={24} type="Close" />
          </Pressable>
        </XStack>
        <Stack className="mb-4/2 border-grays-30 border-t-[1px]">
          <Stack className="px-3">
            <Select
              value={
                sortingOptions === 'verified' ? sortType : nonVerifiedSortType
              }
              mode="single"
              onChange={
                sortingOptions === 'verified'
                  ? handleVerifiedSortingSelection
                  : handleNonVerifiedSortingSelection
              }
            >
              {sortingOptionsList.map(({ value, label }, index) => (
                <Select.Option
                  key={`${value}+${index}`}
                  name={value}
                  showToggle={true}
                  selectedValue={
                    sortingOptions === 'verified'
                      ? sortType
                      : nonVerifiedSortType
                  }
                  optionContainerStyle="h-[62] mt-3 pl-2 w-full justify-center rounded-lg"
                  backgroundColor={{
                    active: 'bg-[#CFE3FF]',
                    inActive: 'bg-[#F5F7FB]',
                  }}
                  iconColor={{
                    active: zetColors.primary[50],
                    inActive: zetColors.grays[60],
                  }}
                  textColor={{
                    active: 'text-grays-80',
                    inActive: 'text-grays-60',
                  }}
                  optionTextStyle="pl-2 pr-4 mr-3"
                >
                  <SH1>{label}</SH1>
                </Select.Option>
              ))}
            </Select>
          </Stack>
        </Stack>
      </BottomSheet>
    </>
  )
}

export default MyCustomers
